/** Takvim modülü — panel açık tema */

import { PANEL, PANEL_PRIMITIVE } from "./panelTokens";

export const CALENDAR_EVENT_COLORS = {
  doctor: PANEL.blue.main,
  patient: "#a855f7",
  customDefault: PANEL.blue.main,
};

export const CALENDAR_STATUS_COLORS = {
  answered: PANEL_PRIMITIVE.emerald600,
  expired: PANEL.danger.main,
  planned: PANEL.text.label,
  pending: PANEL.warning.main,
};

export const panelCalendarCardSx = {
  bgcolor: PANEL.surface,
  borderRadius: PANEL.radius.card,
  border: `1px solid ${PANEL.border}`,
  boxShadow: PANEL.shadow.card,
  p: { xs: 2, md: 3 },
  display: "flex",
  flexDirection: "column",
};

export const panelCalendarTitleSx = {
  fontWeight: 800,
  color: PANEL.text.primary,
  letterSpacing: -0.3,
  fontSize: { xs: "1.25rem", md: "1.5rem" },
};

export const panelCalendarToggleGroupSx = {
  bgcolor: PANEL.fill.muted,
  borderRadius: PANEL.radius.chip,
  border: `1px solid ${PANEL.border}`,
  p: 0.25,
  "& .MuiToggleButton-root": {
    color: `${PANEL.text.muted} !important`,
    border: "none",
    borderRadius: `${PANEL.radius.chip} !important`,
    textTransform: "capitalize",
    px: { xs: 1.25, md: 2 },
    py: 0.75,
    fontWeight: 600,
    fontSize: "0.8125rem",
    "&.Mui-selected": {
      bgcolor: `${PANEL.accent.main} !important`,
      color: `${PANEL.accent.contrast} !important`,
      "&:hover": { bgcolor: `${PANEL.accent.hover} !important` },
    },
    "&:hover": {
      bgcolor: PANEL.fill.subtle,
    },
  },
};

export const panelCalendarSidebarToggleGroupSx = {
  ...panelCalendarToggleGroupSx,
  width: "100%",
  "& .MuiToggleButton-root": {
    ...panelCalendarToggleGroupSx["& .MuiToggleButton-root"],
    flex: 1,
  },
};

export const panelCalendarNavIconButtonSx = {
  bgcolor: PANEL.fill.muted,
  border: `1px solid ${PANEL.border}`,
  borderRadius: PANEL.radius.chip,
  color: PANEL.text.body,
  "&:hover": {
    bgcolor: PANEL.fill.subtle,
    borderColor: PANEL.fill.hover,
  },
};

export const panelCalendarTodayButtonSx = {
  color: PANEL.text.body,
  borderColor: PANEL.border,
  borderRadius: PANEL.radius.chip,
  textTransform: "none",
  fontWeight: 600,
  "&:hover": {
    borderColor: PANEL.accent.main,
    bgcolor: PANEL.accent.soft,
  },
};

export const panelCalendarFilterButtonSx = {
  textTransform: "none",
  fontWeight: 700,
  borderColor: PANEL.border,
  color: PANEL.text.body,
  borderRadius: PANEL.radius.chip,
  "&:hover": {
    borderColor: PANEL.accent.main,
    bgcolor: PANEL.accent.soft,
  },
};

export const panelCalendarDayHeaderSx = {
  textAlign: "center",
  fontWeight: 700,
  color: PANEL.text.muted,
  fontSize: "0.75rem",
  letterSpacing: 0.3,
  textTransform: "none",
};

export const panelCalendarDayCellSx = ({ isSelected = false, isToday = false } = {}) => ({
  p: 1.5,
  bgcolor: isSelected ? PANEL.accent.soft12 : PANEL.fill.muted,
  border: `1px solid ${isSelected ? PANEL.accent.softBorder : PANEL.border}`,
  borderRadius: PANEL.radius.cardSm,
  cursor: "pointer",
  display: "flex",
  flexDirection: "column",
  transition: "all 0.15s ease",
  minHeight: 88,
  "&:hover": {
    bgcolor: isSelected ? PANEL.accent.soft18 : PANEL.fill.subtle,
    borderColor: isSelected ? PANEL.accent.main : PANEL.fill.hover,
    transform: "translateY(-1px)",
    boxShadow: PANEL.shadow.card,
  },
  ...(isToday && !isSelected
    ? {
        boxShadow: `inset 0 0 0 1px ${PANEL.accent.softBorder}`,
      }
    : {}),
});

export const panelCalendarDayNumberSx = ({ isToday = false } = {}) => ({
  color: isToday ? PANEL.accent.contrast : PANEL.text.primary,
  bgcolor: isToday ? PANEL.accent.main : "transparent",
  width: 28,
  height: 28,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
  mb: 1,
  fontWeight: isToday ? 800 : 600,
  fontSize: "0.9375rem",
});

export const panelCalendarEmptyDaySx = {
  p: 1,
  bgcolor: PANEL.pageBg,
  borderRadius: PANEL.radius.cardSm,
  border: `1px dashed ${PANEL.border}`,
};

export const panelCalendarWeekCellSx = ({ isToday = false } = {}) => ({
  p: 1,
  bgcolor: PANEL.fill.muted,
  border: `1px solid ${PANEL.border}`,
  borderTop: isToday ? `3px solid ${PANEL.accent.main}` : `1px solid ${PANEL.border}`,
  borderRadius: PANEL.radius.cardSm,
  display: "flex",
  flexDirection: "column",
  gap: 1,
  cursor: "pointer",
  overflowY: "auto",
  minHeight: 420,
});

export const panelCalendarWeekEventSx = (color) => ({
  p: 1,
  borderRadius: PANEL.radius.chip,
  bgcolor: `${color}14`,
  borderLeft: `3px solid ${color}`,
  fontSize: "0.75rem",
  color: PANEL.text.primary,
});

export const panelCalendarYearMonthSx = {
  bgcolor: PANEL.fill.muted,
  borderRadius: PANEL.radius.cardSm,
  border: `1px solid ${PANEL.border}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  transition: "all 0.15s ease",
  minHeight: 96,
  "&:hover": {
    bgcolor: PANEL.accent.soft12,
    borderColor: PANEL.accent.softBorder,
    transform: "translateY(-1px)",
    boxShadow: PANEL.shadow.card,
  },
};

export const panelCalendarYearMonthLabelSx = {
  fontWeight: 700,
  color: PANEL.text.body,
  fontSize: "1.05rem",
};

export const panelCalendarEventCardSx = (color) => ({
  mb: 2,
  p: 2,
  borderRadius: PANEL.radius.cardSm,
  bgcolor: `${color}10`,
  borderLeft: `4px solid ${color}`,
  transition: "all 0.15s ease",
  minWidth: 0,
  overflow: "hidden",
  "&:hover": { bgcolor: `${color}18` },
});

export const panelCalendarRecentEventCardSx = (color) => ({
  mb: 1.5,
  p: 2,
  borderRadius: PANEL.radius.cardSm,
  bgcolor: `${color}10`,
  borderLeft: `4px solid ${color}`,
  cursor: "pointer",
  transition: "all 0.15s ease",
  minWidth: 0,
  overflow: "hidden",
  "&:hover": { bgcolor: `${color}20`, boxShadow: PANEL.shadow.card },
});

export const panelCalendarEventTitleSx = {
  fontWeight: 700,
  color: PANEL.text.primary,
  lineHeight: 1.25,
  fontSize: "0.9375rem",
};

export const panelCalendarEventMetaSx = {
  color: PANEL.text.muted,
  fontSize: "0.8125rem",
};

export const panelCalendarEventRoleSx = {
  fontSize: "0.8em",
  fontWeight: 400,
  color: PANEL.text.label,
  ml: 1,
};

export const panelCalendarScrollAreaSx = {
  pr: 0.5,
  "&::-webkit-scrollbar": { width: 6 },
  "&::-webkit-scrollbar-thumb": {
    bgcolor: PANEL.fill.hover,
    borderRadius: 4,
  },
};

export const panelCalendarSectionDividerSx = {
  borderBottom: `1px solid ${PANEL.border}`,
  pb: 2,
  mb: 2,
};

export const panelCalendarEmptyStateSx = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
  opacity: 0.65,
  pt: 5,
};

export const panelCalendarAddButtonSx = {
  bgcolor: PANEL.accent.main,
  color: PANEL.accent.contrast,
  boxShadow: PANEL.accent.shadow25,
  "&:hover": { bgcolor: PANEL.accent.hover },
};

export const panelCalendarPatientChipSx = {
  bgcolor: PANEL.pageBg,
  color: PANEL.text.body,
  fontWeight: 600,
  border: `1px solid ${PANEL.border}`,
  fontSize: "0.75rem",
};

export const panelCalendarStatusChipSx = (statusColor) => ({
  bgcolor: statusColor,
  color: "#fff",
  fontWeight: 700,
  fontSize: "0.7rem",
});

export const panelCalendarFilterPopoverPaperProps = {
  sx: {
    p: 3,
    width: 360,
    maxWidth: "calc(100vw - 32px)",
    bgcolor: PANEL.surface,
    borderRadius: PANEL.radius.cardSm,
    mt: 1,
    border: `1px solid ${PANEL.border}`,
    boxShadow: PANEL.shadow.elevatedMini,
  },
};

export const panelCalendarFilterTitleSx = {
  fontWeight: 800,
  mb: 2,
  color: PANEL.text.primary,
  fontSize: "1.05rem",
};

export const panelCalendarFilterLabelSx = {
  color: PANEL.text.muted,
  mb: 1,
  fontWeight: 600,
  fontSize: "0.8125rem",
};

export const panelCalendarFilterFieldSx = {
  "& .MuiOutlinedInput-root": {
    bgcolor: PANEL.surface,
    borderRadius: PANEL.radius.input,
  },
};

export const panelCalendarDateBadgeSx = {
  flexShrink: 0,
  fontSize: "0.75rem",
  fontWeight: 700,
  color: PANEL.blue.text,
};

export const panelCalendarColorSwatchSx = (color, selected) => ({
  width: 32,
  height: 32,
  borderRadius: "50%",
  bgcolor: color,
  cursor: "pointer",
  border: selected ? `2px solid ${PANEL.text.primary}` : "2px solid transparent",
  boxShadow: selected ? `0 0 0 2px ${PANEL.surface}, 0 0 0 4px ${color}` : "none",
  transition: "all 0.15s ease",
});
